// src/pages/PaymentPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard, Shield, Lock, CheckCircle, Smartphone
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';

export default function PaymentPage() {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="payment-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      <div className="page-hero">
        <div className="container">
          <CreditCard size={48} className="hero-icon" />
          <h1>Ödeme Seçenekleri</h1>
          <p>Güvenli ve kolay ödeme yöntemleri</p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="security-banner">
            <Shield size={32} />
            <div>
              <h3>256-bit SSL Güvenli Ödeme</h3>
              <p>Tüm ödeme bilgileriniz şifrelenmiş olarak iletilir</p>
            </div>
          </div>

          <div className="content-section">
            <h2>Ödeme Yöntemlerimiz</h2>
            <div className="payment-methods">
              <div className="method-card">
                <div className="method-icon">
                  <CreditCard size={32} />
                </div>
                <h3>Kredi Kartı</h3>
                <p>Visa, Mastercard, American Express</p>
                <ul>
                  <li>3D Secure güvenlik</li>
                  <li>Taksit imkanı</li>
                  <li>Anında onay</li>
                </ul>
              </div>

              <div className="method-card">
                <div className="method-icon">
                  <CreditCard size={32} />
                </div>
                <h3>Banka Kartı</h3>
                <p>Debit kart ile ödeme</p>
                <ul>
                  <li>Anında ödeme</li>
                  <li>3D Secure güvenlik</li>
                  <li>Tek çekim</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="content-section">
            <h2>Güvenlik</h2>
            <div className="security-grid">
              <div className="security-card">
                <Lock size={32} />
                <h4>SSL Sertifikası</h4>
                <p>256-bit şifreleme ile tüm verileriniz güvende</p>
              </div>
              <div className="security-card">
                <Shield size={32} />
                <h4>3D Secure</h4>
                <p>Banka onaylı güvenli ödeme sistemi</p>
              </div>
              <div className="security-card">
                <CheckCircle size={32} />
                <h4>PCI-DSS</h4>
                <p>Uluslararası ödeme güvenlik standardı</p>
              </div>
            </div>
          </div>

          <div className="cta-box">
            <h3>Ödeme ile ilgili sorularınız mı var?</h3>
            <button className="btn-primary" onClick={() => navigate('/faq')}>
              SSS'ye Göz Atın
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { margin: 0; padding: 0; box-sizing: border-box; }
      .payment-page { min-height: 100vh; background: #f8f9fa; }
      .container { max-width: 1320px; margin: 0 auto; padding: 0 20px; }
      
      .page-hero {
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        padding: 80px 0;
        text-align: center;
        color: #fff;
      }
      .hero-icon { margin-bottom: 24px; opacity: 0.9; }
      .page-hero h1 { font-size: 48px; font-weight: 800; margin-bottom: 16px; }
      .page-hero p { font-size: 18px; opacity: 0.8; }
      
      .page-content { padding: 60px 0; }
      
      .security-banner {
        background: linear-gradient(135deg, #06d6a0, #00b894);
        color: #fff;
        padding: 32px;
        border-radius: 16px;
        display: flex;
        align-items: center;
        gap: 24px;
        margin-bottom: 48px;
      }
      .security-banner h3 { font-size: 24px; margin-bottom: 8px; }
      
      .content-section { margin-bottom: 48px; }
      .content-section h2 { 
        font-size: 32px; 
        font-weight: 800; 
        margin-bottom: 24px;
        text-align: center;
      }
      
      .payment-methods {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
        max-width: 700px;
        margin: 0 auto;
      }
      .method-card {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        text-align: center;
        transition: all 0.3s;
      }
      .method-card:hover {
        transform: translateY(-8px);
        box-shadow: 0 12px 32px rgba(0,0,0,0.12);
      }
      .method-icon {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        color: #fff;
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 20px;
      }
      .method-card h3 { font-size: 20px; margin-bottom: 8px; }
      .method-card p { color: #868e96; margin-bottom: 16px; }
      .method-card ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
        text-align: left;
      }
      .method-card li {
        color: #495057;
        padding-left: 20px;
        position: relative;
        font-size: 14px;
      }
      .method-card li::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #06d6a0;
        font-weight: 700;
      }
      
      .security-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
        max-width: 1000px;
        margin: 0 auto;
      }
      .security-card {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        text-align: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        transition: all 0.3s;
      }
      .security-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.08);
      }
      .security-card svg {
        color: #E5332A;
        margin-bottom: 16px;
      }
      .security-card h4 { font-size: 18px; margin-bottom: 8px; }
      .security-card p { color: #868e96; line-height: 1.6; }
      
      .cta-box {
        background: linear-gradient(135deg, #E5332A, #ff4436);
        padding: 48px;
        border-radius: 24px;
        text-align: center;
        color: #fff;
        margin-top: 48px;
      }
      .cta-box h3 { font-size: 28px; margin-bottom: 24px; }
      .btn-primary {
        padding: 14px 32px;
        background: #fff;
        color: #E5332A;
        border: none;
        border-radius: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.3s;
      }
      .btn-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0,0,0,0.2);
      }
      
      @media (max-width: 992px) {
        .payment-methods { 
          grid-template-columns: 1fr; 
          max-width: 400px;
        }
        .security-grid { 
          grid-template-columns: 1fr;
          max-width: 400px;
        }
      }

      @media (max-width: 768px) {
        .page-hero h1 {
          font-size: 32px;
        }

        .content-section h2 {
          font-size: 24px;
        }

        .security-banner {
          flex-direction: column;
          text-align: center;
        }

        .cta-box {
          padding: 32px 24px;
        }
      }
    `}</style>
  );
}